import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { localizeTo } from "plos/src/components/links/utils";
import PageTitle from "plos/src/components/PageTitle";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLanguageFromPath } from "plos/src/hooks/useLanguageFromPath";
import {
  contentMain,
  homeImage,
  homeImageContainer,
  homeStep,
  homeSteps,
  pagePadding,
} from "plos/src/layouts";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router";
import { useTrackingContext } from "../contexts/TrackingContext";
import competeAgainstFriends from "../img/home/compete-against-friends.png";
import competeAgainstFriendsx2 from "../img/home/compete-against-friends_x2.png";
import createLeagues from "../img/home/create-and-join-leagues.png";
import createLeaguesx2 from "../img/home/create-and-join-leagues_x2.png";
import pickYourSquad from "../img/home/pick-your-squad.png";
import pickYourSquadx2 from "../img/home/pick-your-squad_x2.png";
import { HelmetHead } from "./HelmetHead";
import LatestAlert from "./LatestAlert";
import { LoginPromo } from "./Login";
import { NewsPlaylist } from "./news/NewsPlaylist";
import { NewsSection } from "./news/NewsSection";

const Home = () => {
  const { t } = useTranslation("home");
  const { firePageViewEvent } = useTrackingContext();
  const lang = useLanguageFromPath();

  const player = useSelector(getPlayerData);
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  const now = useSelector(getCurrentEvent);
  const next = useSelector(getNextEvent);

  // Only track if the user is not logged in, otherwise redirected
  useEffect(() => {
    if (!player) {
      firePageViewEvent("fantasy home");
    }
  }, [player]);

  // Dev mode only ability to throw an error for sentry testing
  if (import.meta.env.DEV) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("testError") === "true") {
      throw new Error("Test route error");
    }
  }

  // Delay rendering until entry is available
  if (player && player.entry && !entry) {
    return null;
  }

  // Managers without an entry go straight to Squad Selection if there's an upcoming event
  if (player && !entry && next) {
    return <Navigate to={localizeTo("/squad-selection", lang)} replace />;
  }

  // Managers go to Points if during an active GW and not a new team, Pick Team when there's an upcoming event, or to Status when there's no upcoming event
  if (player && entry) {
    if (now?.finished === false && entry.started_event <= now.id) {
      return (
        <Navigate
          to={localizeTo(`/entry/${player.entry}/event/${now.id}`, lang)}
          replace
        />
      );
    }
    if (next) {
      return <Navigate to={localizeTo("/my-team", lang)} replace />;
    } else {
      return <Navigate to={localizeTo("/status", lang)} replace />;
    }
  }

  return (
    <>
      <HelmetHead
        title={t(
          "metadata.title",
          "Fantasy Premier League, Official Fantasy Football Game of the Premier League",
        )}
        description={t(
          "metadata.description",
          "Official Fantasy Premier League 2026/27. Free to play fantasy football game, set up your fantasy football team at the Official Premier League site.",
        )}
      />
      <div className={pagePadding}>
        <div className={contentMain}>
          <div className={visuallyHidden}>
            <PageTitle title={t("home.pageTitle", "Home")} />
          </div>
          <LatestAlert />
          <LoginPromo />
          <div className={homeSteps}>
            <div className={homeStep}>
              <SurfaceContainer>
                <div className={contentMain}>
                  <div className={homeImageContainer}>
                    <img
                      srcSet={`${pickYourSquad}, ${pickYourSquadx2} 2x`}
                      alt=""
                      className={homeImage}
                    />
                  </div>
                  <Subheading>
                    {t("firstContainer.title", "Pick Your Squad")}
                  </Subheading>
                  <p>
                    {t(
                      "firstContainer.description",
                      "Use your budget of £100m to pick a squad of 15 players from the Premier League.",
                    )}
                  </p>
                </div>
              </SurfaceContainer>
            </div>

            <div className={homeStep}>
              <SurfaceContainer>
                <div className={contentMain}>
                  <div className={homeImageContainer}>
                    <img
                      srcSet={`${createLeagues}, ${createLeaguesx2} 2x`}
                      alt=""
                      className={homeImage}
                    />
                  </div>
                  <Subheading>
                    {t("secondContainer.title", "Create and Join Leagues")}
                  </Subheading>
                  <p>
                    {t(
                      "secondContainer.description",
                      "Play against friends and family, colleagues or a web community in invitational leagues and cups.",
                    )}
                  </p>
                </div>
              </SurfaceContainer>
            </div>

            <div className={homeStep}>
              <SurfaceContainer>
                <div className={contentMain}>
                  <div className={homeImageContainer}>
                    <img
                      srcSet={`${competeAgainstFriends}, ${competeAgainstFriendsx2} 2x`}
                      alt=""
                      className={homeImage}
                    />
                  </div>
                  <Subheading>
                    {t("thirdContainer.title", "Compete Against Friends")}
                  </Subheading>
                  <p>
                    {t(
                      "thirdContainer.description",
                      "Play against friends and family, colleagues or a web community in invitational leagues and cups.",
                    )}
                  </p>
                </div>
              </SurfaceContainer>
            </div>
          </div>
          <NewsSection title={t("newsTitle", "Latest from The Scout")!}>
            <NewsPlaylist
              layout="scrollableRow"
              playlistId={4349947}
              variant="scrollable"
            />
          </NewsSection>
        </div>
      </div>
    </>
  );
};

export default Home;
